import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Backdrop, CircularProgress } from "@mui/material";
import LOgo from "../../assets/Ntact.png";
import moment from "moment";
import Footer from "../../auth/Footer";
import api from "../../api/api";
import { SUBCONTRACTOR_ROUTES } from "./routes";

const SubcontractorRetrieve = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone"); // "phone" | "otp"
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [loader, setLoader] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const inputsRef = useRef([]);
  const [draftIds, setDraftIds] = useState([]);
  const [draftForms, setDraftForms] = useState([]);
  const [showDraftPicker, setShowDraftPicker] = useState(false);

  // Countdown for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  const handleSendOtp = async () => {
    setError("");
    if (!phone || phone.replace(/\D/g, "").length < 7) {
      setError("Please enter a valid mobile number.");
      return;
    }
    setLoader(true);
    try {
      const resp = await api.post("/v1/subcontractor/send_otp", {
        phone_number: phone,
        is_new: "R",
      });
      if (resp.status === 200) {
        setStep("otp");
        setResendTimer(59);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "No draft forms found for this phone number.";
      setError(msg);
    } finally {
      setLoader(false);
    }
  };

  const handleOtpChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[idx] = val.slice(-1);
    setOtp(next);
    if (val && idx < 3) inputsRef.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    setError("");
    const code = otp.join("");
    if (code.length !== 4) {
      setError("Please enter the full 4-digit code.");
      return;
    }
    setLoader(true);
    try {
      const resp = await api.post("/v1/subcontractor/validate_otp", {
        phone_number: phone,
        otp: code,
        flag: "R",
      });

      if (resp.status === 200) {
        const ids = resp.draft_ids || [];
        sessionStorage.setItem("subcontractorToken", resp.access_token || "guest");

        if (ids.length === 0) {
          setError("No draft forms found for this phone number.");
          return;
        }

        if (ids.length === 1) {
          // Single draft — navigate directly
          sessionStorage.setItem("subcontractorSubId", ids[0]);
          navigate(`${SUBCONTRACTOR_ROUTES.form}?mode=continue&allow=guest&phone=${encodeURIComponent(phone)}`);
        } else {
          // Multiple drafts — fetch details and show picker
          setDraftIds(ids);
          const drafts = [];
          for (const id of ids) {
            try {
              const r = await api.get(`/v1/subcontractor/get?id=${id}`);
              if (r.status === 200 && r.data) {
                drafts.push(r.data);
              }
            } catch (_) {
              // skip if fetch fails for a draft
            }
          }
          setDraftForms(drafts);
          setShowDraftPicker(true);
        }
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  const handleSelectDraft = (draft) => {
    sessionStorage.setItem("subcontractorSubId", draft.id);
    navigate(`${SUBCONTRACTOR_ROUTES.form}?mode=continue&allow=guest&phone=${encodeURIComponent(phone)}`);
  };

  const handleResend = () => {
    if (resendTimer > 0) return;
    handleSendOtp();
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Backdrop open={loader} sx={{ zIndex: 9999 }}>
        <CircularProgress />
      </Backdrop>

      <div className="flex flex-grow items-start justify-center px-4 py-10">
        <div className="w-full max-w-3xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <img src={LOgo} alt="NTACT" className="h-12" />
            <h1 className="text-lg md:text-xl font-bold text-center flex-1">
              Subcontractor Safety Pre-Qualification Form
            </h1>
            <div className="w-12" />
          </div>

          {/* Document Info */}
          <div className="flex justify-between text-xs text-gray-600 mb-8 border-b pb-3">
            <div className="flex flex-col text-start">
              <div className="font-semibold text-[#000] leading-5 text-xs">
                Document Name:
              </div>
              <div className="font-normal text-[#000] leading-5 text-xs">
                Subcontractor Safety Pre-
                <br /> Qualification Form
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-[#000] leading-5 text-xs">
                Section:
              </div>
              <div className="font-normal text-[#000] leading-5 text-xs">
                HSE-18
              </div>
            </div>
            <div className="text-right">
              <div className="font-semibold text-[#000] leading-5 text-xs">
                Revision: 1
              </div>
              <div className="font-normal text-[#000] leading-5 text-xs">
                Date: {moment().format("MM-DD-YYYY")}
              </div>
              <div className="font-normal text-[#000] leading-5 text-xs">
                Document Number: 18-7-A
              </div>
            </div>
          </div>

          {/* Card */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            {step === "phone" ? (
              <>
                <h2 className="text-xl font-semibold text-gray-900 text-left mb-1">
                  Retrieve Your Progress
                </h2>
                <p className="text-sm text-gray-500 text-left mb-5">
                  Enter your mobile number to receive a one-time password (OTP).
                </p>

                <label className="text-sm font-medium text-left block mb-1">
                  Mobile Number
                </label>
                <div className="flex items-stretch border border-gray-300 rounded-md overflow-hidden mb-2">
                  {/* <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="bg-gray-50 border-r border-gray-300 px-2 text-sm outline-none"
                  >
                    <option value="+1">+1</option>
                    <option value="+91">+91</option>
                    <option value="+44">+44</option>
                    <option value="+61">+61</option>
                  </select> */}
                  <input
                    type="tel"
                    value={phone}
                    maxLength={10}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (val.length <= 10) setPhone(val);
                      setError("");
                    }}
                    placeholder="XXX XXX XXXX"
                    className="flex-1 px-3 py-2 text-sm outline-none"
                  />
                </div>
                <p className="text-xs text-gray-400 text-left mb-5">
                  We'll send a one-time code to verify your identity.
                </p>

                {error && (
                  <div className="text-red-600 text-sm mb-3 text-left">
                    {error}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(SUBCONTRACTOR_ROUTES.login)}
                    className="flex-1 border border-gray-300 bg-white text-gray-700 text-sm font-medium py-3 rounded-md hover:bg-gray-100 transition"
                  >
                    &lt; Back
                  </button>
                  <button
                    onClick={handleSendOtp}
                    disabled={loader}
                    className="flex-[2] bg-gray-900 text-white text-sm font-medium py-3 rounded-md hover:bg-gray-800 transition disabled:opacity-60"
                  >
                    Send OTP
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-gray-900 text-left mb-1">
                  Verify Your Number
                </h2>
                <p className="text-sm text-gray-500 text-left mb-5">
                  Enter the 4-digit code sent to {countryCode} {phone}
                </p>

                <div className="flex justify-center gap-3 mb-4">
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputsRef.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="w-12 h-14 text-center text-lg border border-gray-300 rounded-md focus:outline-none focus:border-gray-800"
                    />
                  ))}
                </div>

                <div className="text-center text-xs text-gray-500 mb-5">
                  Didn't receive a code?{" "}
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendTimer > 0}
                    className={`underline ${
                      resendTimer > 0
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700"
                    }`}
                  >
                    Resend Code
                    {resendTimer > 0
                      ? ` (0:${resendTimer.toString().padStart(2, "0")})`
                      : ""}
                  </button>
                </div>

                {error && (
                  <div className="text-red-600 text-sm mb-3 text-center">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleVerify}
                  disabled={loader}
                  className="w-full bg-gray-900 text-white text-sm font-medium py-3 rounded-md hover:bg-gray-800 transition disabled:opacity-60"
                >
                  Verify & Continue
                </button>

                <button
                  onClick={() => {
                    setStep("phone");
                    setOtp(["", "", "", ""]);
                    setError("");
                  }}
                  className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700"
                >
                  ← Change number
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Draft Picker Modal */}
      {showDraftPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Select a Draft Form</h3>
            <p className="text-sm text-gray-500 mb-4">
              Multiple draft forms found. Select one to continue:
            </p>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {draftForms.map((draft) => (
                <button
                  key={draft.id}
                  onClick={() => handleSelectDraft(draft)}
                  className="w-full text-left border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition"
                >
                  <div className="font-medium text-sm text-gray-900">
                    {draft.firstname || ""} {draft.lastname || ""}{" "}
                    {!draft.firstname && !draft.lastname ? `Draft #${draft.id}` : ""}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {draft.companyinfo?.subcontractorName
                      ? `Company: ${draft.companyinfo.subcontractorName}`
                      : `ID: ${draft.id}`}
                    {draft.created_at && ` | Created: ${moment(draft.created_at).format("MM-DD-YYYY")}`}
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowDraftPicker(false)}
              className="w-full mt-4 text-sm text-gray-500 hover:text-gray-700 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SubcontractorRetrieve;
